import { useEffect, useRef, useState } from 'react';

const testimonials = [
  {
    stars: 5,
    quote: 'We\'d been through two operators who charged the same fee no matter what happened. 360 hit our RevPAR target in the first quarter. First time an operator actually felt like a partner.',
    name: 'Robert K.',
    role: 'Hotel Owner',
    location: 'Fort Myers, FL',
    angle: 'Performance-based fees',
    icon: 'ri-percent-line',
  },
  {
    stars: 5,
    quote: 'We were 18% below our NOI target. 360 identified the gaps in 30 days and we saw measurable improvement by Day 60. The asset management piece was something we didn\'t know we needed.',
    name: 'Maria L.',
    role: 'Private Equity',
    location: 'Sarasota Portfolio',
    angle: 'Asset optimization',
    icon: 'ri-funds-line',
  },
  {
    stars: 5,
    quote: 'I was days from signing a 10-year HMA with no performance triggers. 360 reviewed it in 48 hours, flagged five issues, and renegotiated. That one conversation was worth more than I can quantify.',
    name: 'James T.',
    role: 'Hotel Developer',
    location: 'Daytona Beach, FL',
    angle: 'Contract advisory',
    icon: 'ri-file-text-line',
  },
];

export default function TestimonialsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className={`mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center space-x-2 mb-6">
            <div className="h-px w-8 bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-600 uppercase tracking-[0.25em]">Social Proof</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <h2 className="text-4xl lg:text-5xl font-light text-gray-900 leading-tight">
              Florida Hotel Owners
              <br />
              <span className="italic text-gray-500">Speaking for Themselves</span>
            </h2>
            <p className="text-sm text-gray-400 font-light max-w-xs leading-relaxed">
              Three markets. Three buyer concerns. One consistent outcome.
            </p>
          </div>
        </div>

        {/* Testimonials */}
        <div className="grid lg:grid-cols-3 gap-8">
          {testimonials.map((t, index) => (
            <div
              key={index}
              className={`flex flex-col bg-gray-50 rounded-2xl p-8 border border-gray-100 hover:border-amber-200 hover:bg-amber-50/20 transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Stars */}
              <div className="flex items-center gap-1 mb-6">
                {Array.from({ length: t.stars }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-amber-400 text-base"></i>
                ))}
              </div>

              {/* Quote */}
              <blockquote className="flex-1 text-base text-gray-700 font-light leading-relaxed mb-8 relative">
                <i className="ri-double-quotes-l text-4xl text-amber-200 absolute -top-2 -left-1"></i>
                <span className="relative z-10 pl-4">{t.quote}</span>
              </blockquote>

              {/* Attribution */}
              <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{t.name}</p>
                  <p className="text-xs text-gray-500 font-light">{t.role} — {t.location}</p>
                </div>
                <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-full">
                  <i className={`${t.icon} text-amber-600 text-base`}></i>
                </div>
              </div>

              {/* Conversion angle tag */}
              <div className="mt-4">
                <span className="inline-block text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 font-light">
                  {t.angle}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
